import React from 'react';
import { MLHelper, getPopupContainer } from '@bimser/core';
import Style from '../assets/styles.scss';
import {
	IRelatedDocumentCategory,
	IRelatedDocumentFile,
	IRelatedDocumentsOptions,
	RelatedDocumentAddingMode
} from '../entities';
import { BCButton, BCDropButton, IButtonProps, MenuProps, findMessage } from '@bimser/components';
import IconAdd from '@bimser/icons/16/add';
import IconDMExplorer from '@bimser/icons/16/open';
import IconTreeArrowDown from '@bimser/icons/16/tree-arrow-down';
import IconClosePanel from '@bimser/icons/16/close-panel';
import IconDelete from '@bimser/icons/16/delete';
import IconDownload from '@bimser/icons/16/download';
import IconViewFile from '@bimser/icons/16/view-file';
import IconRename from '@bimser/icons/16/rename';
import IconWarning from '@bimser/icons/16/warning';

export const RDButtonType = React.forwardRef<RDButtonTypeRef, Props>((props, ref) => {
	const [open, setOpen] = React.useState<boolean>(false);

	const [wrapperWidth, setWrapperWidth] = React.useState<number>();
	const dropdownWrapperRef = React.useRef<HTMLDivElement>({ offsetWidth: 0 } as HTMLDivElement);

	React.useImperativeHandle(ref, () => ({
		onFormResize() {
			setWrapperWidth(dropdownWrapperRef.current.offsetWidth);
		}
	}));

	React.useLayoutEffect(() => {
		setWrapperWidth(dropdownWrapperRef.current.offsetWidth);
	}, [dropdownWrapperRef.current.offsetWidth]);

	React.useEffect(() => {
		if (!props.files.length) {
			setOpen(false);
		}
	}, [props.files]);

	const canAddFromLocal =
		[RelatedDocumentAddingMode.Local, RelatedDocumentAddingMode.All].includes(
			props.addingMode
		) && props.options.addNewEnabled;

	const canAddFromDM =
		[RelatedDocumentAddingMode.DM, RelatedDocumentAddingMode.All].includes(props.addingMode) &&
		props.options.addNewEnabled;

	const getButton = ({ icon, onClick, title }: GetButtonArgs) => {
		const _onClick: IButtonProps['onClick'] = e => {
			e.event.stopPropagation();
			onClick();
		};

		return (
			<BCButton
				onClick={_onClick}
				title={title}
				icon={icon}
				type="text"
			/>
		);
	};

	const getFileItemLabel = (file: IRelatedDocumentFile) => (
		<div className={Style.file}>
			<div
				className={Style.fileName}
				title={file.name || findMessage.get('100810')}
			>
				{props.options.fileDescriptionIsRequired && !file.description && (
					<IconWarning
						height="8"
						width="8"
						title={findMessage.get('101389')}
					/>
				)}
				<span>{file.name || findMessage.get('100810')}</span>
			</div>

			<div>
				{!!file.path &&
					getButton({
						icon: <IconRename />,
						title: findMessage.get('101390'),
						onClick: () => props.onOptionsItemClicked(file.id, 'enterDescription')
					})}
				{!!file.path &&
					props.options.viewable &&
					getButton({
						icon: <IconViewFile />,
						title: findMessage.get('101391'),
						onClick: () => props.onOptionsItemClicked(file.id, 'view')
					})}
				{!!file.path &&
					getButton({
						icon: <IconDownload />,
						title: findMessage.get('101167'),
						onClick: () => props.onOptionsItemClicked(file.id, 'download')
					})}
				{props.options.deleteEnabled &&
					getButton({
						icon: <IconDelete />,
						title: findMessage.get('101218'),
						onClick: () => props.onOptionsItemClicked(file.id, 'unlink')
					})}
			</div>
		</div>
	);

	const getFilesAsItems = (): MenuProps['items'] =>
		props.files.map(file => ({ key: file.id, label: getFileItemLabel(file) }));

	const onClickAdd = () => {
		switch (props.addingMode) {
			case RelatedDocumentAddingMode.DM:
				return (
					canAddFromDM &&
					props.onFileAdd(RelatedDocumentAddingMode.DM, props.category.categoryId)
				);
			case RelatedDocumentAddingMode.Local:
			case RelatedDocumentAddingMode.All:
			default:
				return (
					canAddFromLocal &&
					props.onFileAdd(RelatedDocumentAddingMode.Local, props.category.categoryId)
				);
		}
	};

	const renderAddFileButtons = () => {
		if (props.addingMode === RelatedDocumentAddingMode.All) {
			return (
				<div>
					{canAddFromLocal &&
						getButton({
							icon: <IconAdd />,
							title: findMessage.get('101386'),
							onClick: () =>
								props.onFileAdd(
									RelatedDocumentAddingMode.Local,
									props.category.categoryId
								)
						})}

					{canAddFromDM &&
						getButton({
							icon: <IconDMExplorer />,
							title: findMessage.get('101387'),
							onClick: () =>
								props.onFileAdd(
									RelatedDocumentAddingMode.DM,
									props.category.categoryId
								)
						})}
				</div>
			);
		}
	};

	const buttonsRender = ([left, right]: [React.ReactNode, React.ReactNode]) => [
		left,
		<div
			key="right"
			onClick={() => {
				if (props.files.length) {
					setOpen(!open);
				}
			}}
		>
			{right}
		</div>
	];

	const renderButtonInnerText = () => {
		const categoryName = MLHelper.getMLText(props.category.name);
		const maxCount = props.category.maxFilesCount || '∞';

		return `${categoryName} - ${props.files.length}/${maxCount}`;
	};

	return (
		<div ref={dropdownWrapperRef}>
			<BCDropButton
				disabled={props.disabled || props.options.disabled || props.options.readOnly}
				icon={open ? <IconClosePanel /> : <IconTreeArrowDown />}
				overlayClassName={Style.dropdownStyle}
				overlayStyle={{ width: wrapperWidth }}
				className={Style.dropdownButtonStyle}
				getPopupContainer={getPopupContainer}
				menu={{ items: getFilesAsItems() }}
				buttonsRender={buttonsRender}
				style={{ width: '100%' }}
				onClick={onClickAdd}
				open={open}
			>
				{renderButtonInnerText()}
				{renderAddFileButtons()}
			</BCDropButton>
		</div>
	);
});

type Props = {
	category: IRelatedDocumentCategory;
	files: IRelatedDocumentFile[];
	addingMode: RelatedDocumentAddingMode;
	disabled: boolean;
	options: IRelatedDocumentsOptions;
	onFileAdd: (addKey: RelatedDocumentAddingMode, categoryId: string) => void;
	onOptionsItemClicked: (fileId: string, eventKey: string) => void;
};

type GetButtonArgs = {
	icon: React.ReactElement;
	onClick: () => void;
	title: string;
};

export type RDButtonTypeRef = {
	onFormResize: () => void;
};
